import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { SidebarComponent } from './sidebar/sidebar.component';
import { ApplicationStore, ApplicationStoreType } from '../core/store/application.store';
import { UserService } from '../core/services/user.service';
import { AppUrls } from '../routing/app-urls';
import { environment } from '../../environments/environment';
import { TourGuideComponent } from './tour-guide/tour-guide.component';
import { UserFeatureProgressStore, UserFeatureProgressStoreType } from '../core/store/user-feature-progress.store';
import { UserFeatureProgressService } from '../core/services/user-feature-progress.service';
import { UserFeatureType } from '../shared/types/user-feature-progress';
import { OrganizationService } from '../core/services/organization.service';
import { Role } from '../shared/types/role';
import { filter, switchMap, take } from 'rxjs';

declare const heap: any;

@Component({
    selector: 'juyo-shell-layout',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, SidebarComponent, TourGuideComponent],
    styleUrl: 'shell.layout.scss',
    template: `
        <juyo-sidebar [collapsed]="collapsed()" (toggled)="onToggle()" />
        <div class="content">
            <router-outlet />
        </div>
        @if (showTour()) {
            <juyo-tour-guide (closed)="onCompleteTour()" />
        }
    `,
})
export class ShellLayout {
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private userService = inject(UserService);
    private organizationService = inject(OrganizationService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private userFeatureProgressStore: UserFeatureProgressStoreType = inject(UserFeatureProgressStore);
    private userFeatureProgressService = inject(UserFeatureProgressService);

    protected collapsed = signal(false);
    protected showTour = signal(false);

    constructor() {
        if (history.state?.showTour) {
            this.showTour.set(true);
            history.replaceState({}, document.title);
        }
        if (!this.applicationStore.signedInUser()) {
            this.userService.loadUser().subscribe();
        }
        this.organizationService.getOrganizations().subscribe((organizations) => {
            if (!organizations || organizations.length === 0) {
                this.router.navigate([AppUrls.noAccessUrl]);
            }
        });

        toObservable(this.applicationStore.selectedOrganizationAccess)
            .pipe(
                filter((access) => access?.userRole === Role.OrganizationOwner && !access.fromV3),
                take(1),
                switchMap(() => this.userFeatureProgressService.getUserFeatureProgress(UserFeatureType.Onboarding)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (progress) => this.userFeatureProgressStore.setProgress(UserFeatureType.Onboarding, progress),
                error: (err) => console.error('Failed to load onboarding progress', err),
            });

        if (environment.heapId) {
            const effectRef = effect(() => {
                const user = this.applicationStore.signedInUser();
                const selectedOrganization = this.applicationStore.selectedOrganization();

                if (user && selectedOrganization) {
                    heap.identify(user.id);
                    heap.addUserProperties({
                        email: `${user.email.toLowerCase()}`,
                        department: user.department,
                        organizationId: selectedOrganization.id,
                        organization: selectedOrganization.name,
                        chainId: selectedOrganization.chainId,
                    });
                    effectRef.destroy();
                }
            });
        }
    }

    protected onToggle(): void {
        this.collapsed.update((c) => !c);
    }

    protected onCompleteTour(): void {
        this.showTour.set(false);
        this.router.navigate(['/', AppUrls.organizationSettingsUrl, AppUrls.organizationSettingsPropertiesPath]);
    }
}
